/* eslint-disable no-param-reassign */
import React, { useState } from 'react';
import Image from 'next/image';
import Skeleton from '@material-ui/lab/Skeleton';
import { getThumborUrl } from '@root/src/helpers/thumborConfig';

// A simple breakpoint to match what was previously used in pwa-mitra10
const BREAKPOINTS = {
    sm: 768,
};

const generateThumborUrl = (src, width, quality) => {
    const thumborBaseUrl = getThumborUrl();
    if (!thumborBaseUrl) return src;
    
    // Check if thumborBaseUrl already has formatting/quality included (as in swift-page's config)
    if (thumborBaseUrl.includes('filters:format(webp)')) {
        return `${thumborBaseUrl}/${src}`;
    }

    return `${thumborBaseUrl}/unsafe/${width}x0/filters:format(webp):quality(${quality || 80})/${src}`;
};

const CustomImage = ({
    src,
    width = 500,
    height = 500,
    srcMobile,
    widthMobile,
    heightMobile,
    alt = 'Image',
    className = '',
    style = {},
    lazy = true,
    preload = false,
    quality = 80,
    classContainer = '',
    
    contentWidth,
    isHomepageBanner,
    customClass,
    slideCount,
    currentSlide,
    
    loader, 
    unoptimized,
    
    ...other
}) => {
    const [isLoadedDesktop, setIsLoadedDesktop] = useState(false);
    const [isLoadedMobile, setIsLoadedMobile] = useState(false);

    const commonStyle = {
        width: '100%',
        height: 'auto',
        objectFit: 'cover',
        ...style,
    };

    const finalSrcDesktop = generateThumborUrl(src, width, quality);
    const finalSrcMobile = srcMobile 
        ? generateThumborUrl(srcMobile, widthMobile || width, quality) 
        : null;

    const isFill = other.layout === 'fill';

    const imagePropsDesktop = {
        unoptimized: true,
        alt,
        ...(!isFill && { width, height }),
        layout: other.layout || 'responsive',
        priority: preload,
        className,
        style: commonStyle,
        onLoad: () => setIsLoadedDesktop(true),
        onError: () => setIsLoadedDesktop(true),
        ...other,
    };

    const imagePropsMobile = {
        unoptimized: true,
        alt,
        ...(!isFill && { width: widthMobile || width, height: heightMobile || height }),
        layout: other.layout || 'responsive',
        priority: preload,
        className,
        style: commonStyle,
        onLoad: () => setIsLoadedMobile(true),
        onError: () => setIsLoadedMobile(true),
        ...other,
    };

    if (srcMobile) {
        return (
            <>
                {/* Desktop View */}
                <div className={`hidden-mobile ${classContainer}`} style={{ position: 'relative', width: '100%' }}>
                    {!isLoadedDesktop && (
                        <Skeleton 
                            variant="rect" 
                            width="100%" 
                            height="100%" 
                            animation="wave"
                            style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1 }}
                        />
                    )}
                    <Image
                        {...imagePropsDesktop}
                        src={finalSrcDesktop}
                    />
                </div>
                
                {/* Mobile View */}
                <div className={`visible-mobile ${classContainer}`} style={{ position: 'relative', width: '100%' }}>
                    {!isLoadedMobile && (
                        <Skeleton 
                            variant="rect" 
                            width="100%" 
                            height="100%" 
                            animation="wave"
                            style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1 }}
                        />
                    )}
                    <Image
                        {...imagePropsMobile}
                        src={finalSrcMobile}
                    />
                </div>

                <style jsx>{`
                    .hidden-mobile { display: none; }
                    .visible-mobile { display: block; }
                    @media (min-width: ${BREAKPOINTS.sm}px) {
                        .hidden-mobile { display: block; }
                        .visible-mobile { display: none; }
                    }
                `}</style>
            </>
        );
    }
    
    return (
        <div className={classContainer} style={{ position: 'relative', width: '100%' }}>
            {!isLoadedDesktop && (
                <Skeleton 
                    variant="rect" 
                    width="100%" 
                    height="100%" 
                    animation="wave"
                    style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1 }}
                />
            )}
            <Image
                {...imagePropsDesktop}
                src={finalSrcDesktop}
            />
        </div>
    );
};

export default CustomImage;
